import { IProductNavigation, IProductSideNav, ProductNavigationModule } from '@adaptiva/core/app-shell';
import { provideChartComponents } from '@adaptiva/core/charts';
import { UserScriptingService } from '@adaptiva/core/code';
import { ErrorHandlerService } from '@adaptiva/core/error-handler';
import { FilterTransformerModule } from '@adaptiva/core/filters';
import { Licensing, provideLicenseComponents } from '@adaptiva/core/licensing';
import { DynamicComponentLoaderModule, LazyLoadingModule } from '@adaptiva/core/loaders';
import { MenuControllerModule } from '@adaptiva/core/menus';
import { adaptivaUiHeaderInterceptor, connectivityInterceptor, httpParamsInterceptor, timeoutInterceptor } from '@adaptiva/core/net';
import { INotificationService } from '@adaptiva/core/notifications';
import { SelectorOverlayModule } from '@adaptiva/core/overlays';
import { requestPoolInterceptor } from '@adaptiva/core/resource-pool';
import { AdaptivaTitleStrategy, TITLE_SUFFIX, TitleService } from '@adaptiva/core/routing';
import { SummarizeModule } from '@adaptiva/core/summary';
import { BaseThemeService } from '@adaptiva/core/themes';
import { ThreadingModule, ThreadWorker } from '@adaptiva/core/threading';
import { TreeControllerModule } from '@adaptiva/core/trees';
import { AdaptivaAuthPlugin, authInterceptor, EJavaClassNames, GlobalEditorServiceModule, LicenseManagerModule, ObjectManagerServiceModule, provideAuthPlugins, provideBrowserConfigFactories, provideOnboardingPanels, ServerNotificationService, ThemeService, xsrfInterceptor } from '@adaptiva/data-service';
import { FormFieldCustomizerServiceModule, FormScriptingModule } from '@adaptiva/forms/common';
import { FORM_FIELD_DESIGNER_COMPONENT_MAP } from '@adaptiva/forms/widgets';
import { provideEditorInputFactoryService } from '@adaptiva/server/editors';
import { AppShellComponentModule } from '@adaptiva/widgets/app-shell';
import { CHART_COMPONENT_RECORD } from '@adaptiva/widgets/charts';
import { EximManagerModule } from '@adaptiva/widgets/object-transfer';
import { PlatformModule } from '@angular/cdk/platform';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { ErrorHandler, forwardRef, NgModule } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { RouterModule, TitleStrategy } from '@angular/router';
import { ServiceWorkerModule } from '@angular/service-worker';
import { NgxChartsModule } from '@swimlane/ngx-charts';
import { MonacoEditorModule } from 'ngx-monaco-editor-v2';
import { environment } from '../environments/environment';
import { TenableAuthPlugin } from './authentication';
import { AdminPortalAppShellComponentModule } from './components';
import { LAZY_LOADED_CLASSES, LAZY_LOADED_REGISTRATIONS, PLATFORM_BROWSER_CONFIG_FACTORIES, PLATFORM_CAN_SUMMON_CHECKS, PLATFORM_CANNOT_CREATE_CLASSNAMES } from './constants';
import { PLATFORM_EXIM_RESOLVER_OVERLAYS } from './constants/exim';
import { ADMIN_PORTAL_MENU_CONTROLLERS } from './constants/menus';
import * as Navigation from './constants/navigation';
import { ADMIN_PORTAL_SUMMARIZABLE_CLASSES } from './constants/summarize';
import { ADMIN_PORTAL_TREE_CONTROLLERS } from './constants/trees';
import { DYNAMIC_COMPONENT_MANIFESTS } from './dynamic-components.manifest';
import { AdminPortalUserScriptService } from './services';
import { LoginModule } from './views/login';

const PRODUCT_NAVIGATION: IProductNavigation[] = [
	Navigation.ENDPOINT_HEALTH_PRODUCT_NAVIGATION,
	Navigation.ONESITE_ANYWHERE_PRODUCT_NAVIGATION,
	Navigation.ONESITE_INTUNE_PRODUCT_NAVIGATION,
	Navigation.ONESITE_VMWARE_PRODUCT_NAVIGATION,
	Navigation.PATCHING_PRODUCT_NAVIGATION,
	Navigation.INVENTORY_PRODUCT_NAVIGATION,
	Navigation.UXM_PRODUCT_NAVIGATION
];

const GLOBAL_SIDENAV: IProductSideNav[] = [
	Navigation.PLATFORM_SIDENAV_ITEMS
];

const IMPORTS: any[] = [
	AdminPortalAppShellComponentModule,
	AppShellComponentModule,
	BrowserAnimationsModule,
	BrowserModule,
	DynamicComponentLoaderModule.forRoot(DYNAMIC_COMPONENT_MANIFESTS),
	EximManagerModule.register(PLATFORM_EXIM_RESOLVER_OVERLAYS),
	FilterTransformerModule.forRoot(new Map()),
	FormFieldCustomizerServiceModule.forRoot(FORM_FIELD_DESIGNER_COMPONENT_MAP),
	FormScriptingModule.forRoot(),
	GlobalEditorServiceModule.forRoot({
		canEditChecks: PLATFORM_CAN_SUMMON_CHECKS,
		classesDisallowedForAnywhereEditor: PLATFORM_CANNOT_CREATE_CLASSNAMES
	}),
	LazyLoadingModule.forRoot({
		loaders: LAZY_LOADED_CLASSES,
		registrations: LAZY_LOADED_REGISTRATIONS,
	}),
	LicenseManagerModule.use(),
	LoginModule,
	MenuControllerModule.forRoot(ADMIN_PORTAL_MENU_CONTROLLERS),
	MonacoEditorModule.forRoot({
		baseUrl: window.location.origin + '/assets/monaco/min/vs',
		defaultOptions: {
			scrollBeyondLastLine: false,
			fixedOverflowWidgets: true
		}
	}),
	NgxChartsModule,
	ObjectManagerServiceModule.forRoot({ uncachedClasses: new Set([EJavaClassNames.LicenseKeyContainerObject]) }),
	PlatformModule,
	ProductNavigationModule.register(PRODUCT_NAVIGATION, GLOBAL_SIDENAV),
	RouterModule,
	ServiceWorkerModule.register('sw.js', { enabled: environment.production }),
	SummarizeModule.register(ADMIN_PORTAL_SUMMARIZABLE_CLASSES),
	ThreadingModule.register(
		new Map<string, () => ThreadWorker>([
			['is-equal', () => new SharedWorker(new URL('./workers/is-equal.worker', import.meta.url))],
			['markdown', () => new SharedWorker(new URL('./workers/markdown-processor.worker', import.meta.url))],
		])
	),
	TreeControllerModule.forRoot({
		controllers: ADMIN_PORTAL_TREE_CONTROLLERS,
	}),
	SelectorOverlayModule.forRoot(new Map()),
];

@NgModule({
	imports: IMPORTS,
	exports: IMPORTS,
	providers: [
		TitleService,
		provideBrowserConfigFactories(PLATFORM_BROWSER_CONFIG_FACTORIES),
		provideEditorInputFactoryService(),
		provideHttpClient(
			withInterceptors([
				authInterceptor(3),
				connectivityInterceptor(),
				adaptivaUiHeaderInterceptor,
				requestPoolInterceptor(),
				timeoutInterceptor,
				httpParamsInterceptor,
				xsrfInterceptor,
			])
		),
		provideAuthPlugins({
			[Licensing.Products.Internal.ADAPTIVA]: AdaptivaAuthPlugin,
			[Licensing.Products.AddOns.TENABLE]: TenableAuthPlugin,
		}),
		provideChartComponents(CHART_COMPONENT_RECORD),
		provideOnboardingPanels(),
		provideLicenseComponents(),
		{ provide: TITLE_SUFFIX, useValue: 'Admin Portal' },
		{ provide: TitleStrategy, useExisting: forwardRef(() => AdaptivaTitleStrategy) },
		{ provide: ErrorHandler, useClass: ErrorHandlerService },
		{ provide: BaseThemeService, useClass: ThemeService },
		{ provide: INotificationService, useClass: ServerNotificationService },
		{ provide: UserScriptingService, useClass: AdminPortalUserScriptService },
	]
})
export class AdminPortalModule {

}
